import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Menu,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconChevronLeft, IconDotsVertical, IconEdit, IconTrash, IconInbox } from "@tabler/icons-react";
import { createFileRoute, Outlet, useNavigate, useMatchRoute } from "@tanstack/react-router";
import {
  useProject,
  useUpdateProject,
  useDeleteProject,
  type UpdateProjectData,
} from "@/hooks/useProjects";
import { AlertBanner } from "@/components/AlertBanner";

export const Route = createFileRoute("/projects/$projectId")({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { data, isLoading } = useProject(projectId);
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  const project = data?.project;

  // Determine active tab based on current route
  const getActiveTab = () => {
    if (matchRoute({ to: "/projects/$projectId/budget" })) return "budget";
    if (matchRoute({ to: "/projects/$projectId/commitments" as any })) return "commitments";
    if (matchRoute({ to: "/projects/$projectId/actuals" as any })) return "actuals";
    if (matchRoute({ to: "/projects/$projectId/cost-dashboard" as any })) return "cost-dashboard";
    if (matchRoute({ to: "/projects/$projectId/cost-codes" })) return "cost-codes";
    if (matchRoute({ to: "/projects/$projectId/daily-logs" })) return "daily-logs";
    if (matchRoute({ to: "/projects/$projectId/inbox" as any })) return "inbox";
    return "overview";
  };

  const form = useForm<UpdateProjectData>({
    initialValues: {
      name: project?.name || "",
      address: project?.address || "",
      city: project?.city || "",
      state: project?.state || "",
      zip: project?.zip || "",
    },
  });

  // Update form when project data loads
  if (project && !isLoading) {
    const currentValues = form.values;
    if (currentValues.name !== project.name) {
      form.setValues({
        name: project.name,
        address: project.address || "",
        city: project.city || "",
        state: project.state || "",
        zip: project.zip || "",
      });
    }
  }

  const handleEdit = form.onSubmit(async (values) => {
    try {
      await updateProject.mutateAsync({ id: projectId, data: values });
      notifications.show({
        title: "Success",
        message: "Project updated successfully!",
        color: "green",
      });
      closeEdit();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update project",
        color: "red",
      });
    }
  });

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(projectId);
      notifications.show({
        title: "Success",
        message: "Project deleted successfully!",
        color: "green",
      });
      navigate({ to: "/projects" });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete project",
        color: "red",
      });
    }
  };

  const handleArchive = async () => {
    try {
      await updateProject.mutateAsync({
        id: projectId,
        data: { status: "archived" },
      });
      notifications.show({
        title: "Success",
        message: "Project archived successfully!",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to archive project",
        color: "red",
      });
    }
  };

  const handleBack = () => {
    navigate({ to: "/projects" });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "archived":
        return "gray";
      default:
        return "gray";
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={300} radius="md" />
        </Stack>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container size="xl" py="xl">
        <Paper p="xl" withBorder>
          <Stack gap="md" align="center">
            <Text size="lg" c="dimmed">
              Project not found
            </Text>
            <Button onClick={handleBack}>Back to Projects</Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Alert Banner */}
        <AlertBanner projectId={projectId} />

        <Group justify="space-between" align="center">
          <Button
            variant="subtle"
            leftSection={<IconChevronLeft size={16} />}
            onClick={handleBack}
          >
            Back to Projects
          </Button>

          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle" size="lg">
                <IconDotsVertical size={20} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item leftSection={<IconEdit size={16} />} onClick={openEdit}>
                Edit Project
              </Menu.Item>
              {project.status === "active" && (
                <Menu.Item onClick={handleArchive}>
                  Archive Project
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={openDelete}
              >
                Delete Project
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Paper p="xl" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Title order={1}>{project.name}</Title>
              <Badge color={getStatusColor(project.status)} variant="light" size="lg">
                {project.status}
              </Badge>
            </Group>

            <Tabs value={getActiveTab()} onChange={(value) => {
              if (value === "overview") {
                navigate({ to: "/projects/$projectId", params: { projectId } });
              } else if (value === "budget") {
                navigate({ to: "/projects/$projectId/budget", params: { projectId } });
              } else if (value === "commitments") {
                navigate({ to: "/projects/$projectId/commitments", params: { projectId } } as any);
              } else if (value === "actuals") {
                navigate({ to: "/projects/$projectId/actuals", params: { projectId } } as any);
              } else if (value === "cost-dashboard") {
                navigate({ to: "/projects/$projectId/cost-dashboard", params: { projectId } } as any);
              } else if (value === "cost-codes") {
                navigate({ to: "/projects/$projectId/cost-codes", params: { projectId } });
              } else if (value === "daily-logs") {
                navigate({ to: "/projects/$projectId/daily-logs", params: { projectId } });
              } else if (value === "inbox") {
                navigate({ to: "/projects/$projectId/inbox", params: { projectId } } as any);
              }
            }}>
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="budget">Budget</Tabs.Tab>
                <Tabs.Tab value="commitments">Commitments</Tabs.Tab>
                <Tabs.Tab value="actuals">Actuals</Tabs.Tab>
                <Tabs.Tab value="cost-dashboard">Cost Dashboard</Tabs.Tab>
                <Tabs.Tab value="cost-codes">Cost Codes</Tabs.Tab>
                <Tabs.Tab value="daily-logs">Daily Logs</Tabs.Tab>
                <Tabs.Tab value="inbox" leftSection={<IconInbox size={16} />}>Inbox</Tabs.Tab>
              </Tabs.List>
            </Tabs>
          </Stack>
        </Paper>

        <Outlet />
      </Stack>

      <Modal opened={editOpened} onClose={closeEdit} title="Edit Project" centered>
        <form onSubmit={handleEdit}>
          <Stack gap="md">
            <TextInput
              label="Project Name"
              placeholder="Enter project name"
              {...form.getInputProps("name")}
              required
            />

            <TextInput
              label="Address"
              placeholder="Enter street address"
              {...form.getInputProps("address")}
            />

            <Group grow>
              <TextInput
                label="City"
                placeholder="City"
                {...form.getInputProps("city")}
              />
              <TextInput
                label="State"
                placeholder="State"
                {...form.getInputProps("state")}
              />
            </Group>

            <TextInput
              label="ZIP Code"
              placeholder="ZIP code"
              {...form.getInputProps("zip")}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeEdit} disabled={updateProject.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={updateProject.isPending}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Delete Project"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this project? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDelete} disabled={deleteProject.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteProject.isPending}>
              Delete Project
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
